import apiClient from './client';
import { ApiResponse, Repository, Commit } from '@/types';

/**
 * Repository query parameters
 */
export interface RepoQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  projectId?: string;
  active?: boolean;
  webhookActive?: boolean;
  search?: string;
}

/**
 * Get repositories with filters
 */
export async function getRepositories(params?: RepoQueryParams): Promise<{
  data: Repository[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}> {
  const response = await apiClient.get<ApiResponse<Repository[]>>('/repos', { params });
  return {
    data: response.data.data || [],
    pagination: (response.data.meta?.pagination as {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    }) || { page: 1, limit: 20, total: 0, totalPages: 0 },
  };
}

/**
 * Get a specific repository
 */
export async function getRepository(id: string): Promise<Repository> {
  const response = await apiClient.get<ApiResponse<Repository>>(`/repos/${id}`);
  return response.data.data!;
}

/**
 * Get commits for a repository
 */
export async function getRepositoryCommits(
  id: string,
  limit?: number
): Promise<Commit[]> {
  const response = await apiClient.get<ApiResponse<Commit[]>>(`/repos/${id}/commits`, {
    params: { limit },
  });
  return response.data.data || [];
}

/**
 * Update repository data
 */
export interface UpdateRepoData {
  name?: string;
  gitlabUrl?: string;
  defaultBranch?: string;
  projectId?: string | null;
  inheritProjectRules?: boolean;
  active?: boolean;
}

export async function updateRepository(
  id: string,
  data: UpdateRepoData
): Promise<Repository> {
  const response = await apiClient.put<ApiResponse<Repository>>(`/repos/${id}`, data);
  return response.data.data!;
}

/**
 * Sync repository from GitLab
 */
export async function syncRepository(
  id: string,
  commitLimit?: number
): Promise<{
  repo: Repository;
  commits: { created: number; skipped: number; failed: number };
  errors: string[];
}> {
  const response = await apiClient.post<
    ApiResponse<{
      repo: Repository;
      commits: { created: number; skipped: number; failed: number };
      errors: string[];
    }>
  >(`/repos/${id}/sync`, { commitLimit });
  return response.data.data!;
}

/**
 * Register webhook for repository
 */
export async function registerWebhook(repoId: string): Promise<{
  repoId: string;
  repoName: string;
  webhookId: number;
  webhookUrl: string;
}> {
  const response = await apiClient.post<
    ApiResponse<{
      repoId: string;
      repoName: string;
      webhookId: number;
      webhookUrl: string;
    }>
  >(`/webhooks/register/${repoId}`);
  return response.data.data!;
}

/**
 * Unregister webhook for repository
 */
export async function unregisterWebhook(repoId: string): Promise<{
  repoId: string;
  repoName: string;
}> {
  const response = await apiClient.delete<
    ApiResponse<{
      repoId: string;
      repoName: string;
    }>
  >(`/webhooks/unregister/${repoId}`);
  return response.data.data!;
}
